// Compare utilities — parse spec strings into numeric values for scoring,
// derive winner badges from a set of products, and run the household-fit
// scorecard against user wizard answers.

import { products } from './products';

// --- Numeric extractors ----------------------------------------------------

const findSpec = (product, regex) => {
  const row = product.specs?.find((s) => regex.test(s.label));
  return row?.value || '';
};

export const getTankL = (p) => parseInt(p.headlineSpec?.value, 10) || 0;

export const getCOP = (p) => {
  const v = findSpec(p, /^COP$/i) || findSpec(p, /COP/i);
  const m = v.match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : 0;
};

export const getNoiseDb = (p) => {
  const v = findSpec(p, /noise/i);
  const m = v.match(/(\d+(?:\.\d+)?)\s*dB/);
  if (m) return parseFloat(m[1]);
  // Handle "< 39 dB" pattern
  const lt = v.match(/<\s*(\d+(?:\.\d+)?)/);
  return lt ? parseFloat(lt[1]) - 0.1 : null; // slightly better than the cap
};

export const getRefrigerant = (p) => {
  const v = findSpec(p, /refrigerant$/i);
  if (/R744|CO2|CO₂/i.test(v)) return 'CO2 (R744)';
  if (/R290/.test(v)) return 'R290';
  if (/R32/.test(v)) return 'R32';
  return v.split('/')[0].trim() || '—';
};

export const getBoostKW = (p) => {
  const v = findSpec(p, /backup|boost/i);
  if (!v || /N\/?A|none|no booster|heat[ -]pump only/i.test(v)) return 0;
  const m = v.match(/(\d+(?:\.\d+)?)\s*kW/);
  return m ? parseFloat(m[1]) : 0;
};

export const getMinAmbientC = (p) => {
  const v = findSpec(p, /ambient|operating/i);
  const m = v.match(/-?\d+(?:\.\d+)?/);
  return m ? parseInt(m[0], 10) : null;
};

export const getTankWarrantyYears = (p) => {
  const w = p.warranty?.find((x) => /tank/i.test(x.component));
  const m = w?.term?.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
};

// --- Winner badges -------------------------------------------------------

// Returns a map: { [productSlug]: ['Best for Efficiency', ...] }
export const getBadges = (selectedProducts) => {
  const map = {};
  if (selectedProducts.length < 2) return map;
  selectedProducts.forEach((p) => (map[p.slug] = []));

  const tryWinner = (label, scoreFn, higherIsBetter = true) => {
    const scored = selectedProducts
      .map((p) => ({ slug: p.slug, v: scoreFn(p) }))
      .filter((s) => s.v !== null && s.v !== 0 && !Number.isNaN(s.v));
    if (scored.length < 2) return;
    scored.sort((a, b) => (higherIsBetter ? b.v - a.v : a.v - b.v));
    const winner = scored[0];
    if (scored[1] && scored[1].v === winner.v) return; // skip ties
    map[winner.slug].push(label);
  };

  tryWinner('Most Efficient', getCOP, true);
  tryWinner('Quietest', getNoiseDb, false);
  tryWinner('Best for Cold Climates', getMinAmbientC, false);
  tryWinner('Largest Tank', getTankL, true);
  tryWinner('Best Warranty', getTankWarrantyYears, true);
  tryWinner('Fastest Recovery', getBoostKW, true);
  return map;
};

// --- Household-fit scorecard --------------------------------------------

// answers = { householdSize: '1-3'|'4-5'|'6+', climate: 'mild'|'cool'|'cold',
//             priority: 'efficiency'|'value'|'quiet'|'longevity',
//             fastRecovery: 'yes'|'no' }

const tankFitScore = (tankL, householdSize) => {
  // Recommended L per person ~ 50L
  const ideal = { '1-3': 180, '4-5': 270, '6+': 340 }[householdSize] || 250;
  const ratio = tankL / ideal;
  if (ratio >= 0.9 && ratio <= 1.2) return 100;
  if (ratio >= 0.75 && ratio <= 1.4) return 80;
  if (ratio >= 0.6 && ratio <= 1.6) return 60;
  return 30;
};

const climateScore = (minAmbientC, climate) => {
  if (minAmbientC === null) return 60;
  const threshold = { mild: 0, cool: -5, cold: -10 }[climate];
  if (minAmbientC <= threshold) return 100;
  if (minAmbientC <= threshold + 3) return 80;
  if (minAmbientC <= threshold + 6) return 60;
  return 40;
};

const priorityScore = (p, priority) => {
  if (priority === 'efficiency') {
    const cop = getCOP(p);
    return Math.min(100, Math.round((cop / 5.7) * 100));
  }
  if (priority === 'quiet') {
    const db = getNoiseDb(p);
    if (db === null) return 60;
    if (db <= 40) return 100;
    if (db <= 43) return 90;
    if (db <= 47) return 75;
    return 60;
  }
  if (priority === 'longevity') {
    const y = getTankWarrantyYears(p);
    return Math.min(100, Math.round((y / 7) * 100));
  }
  // 'value' — proxy: rebate eligible + boost option = better value
  let s = 70;
  if (p.rebateEligible) s += 15;
  if (getBoostKW(p) > 0) s += 5;
  return Math.min(100, s);
};

const recoveryScore = (boostKW, fastRecovery) => {
  if (fastRecovery === 'no') return 80;
  if (boostKW >= 4) return 100;
  if (boostKW >= 2) return 85;
  if (boostKW > 0) return 70;
  return 50;
};

export const scoreProduct = (product, answers) => {
  const tank = tankFitScore(getTankL(product), answers.householdSize);
  const climate = climateScore(getMinAmbientC(product), answers.climate);
  const priority = priorityScore(product, answers.priority);
  const recovery = recoveryScore(getBoostKW(product), answers.fastRecovery);

  // Weighted avg — priority matters most, tank fit second
  const total = Math.round(tank * 0.3 + climate * 0.2 + priority * 0.35 + recovery * 0.15);

  const reasons = [];
  if (tank >= 80) reasons.push(`Tank size matches a ${answers.householdSize.replace('-', '–')} person household`);
  if (climate >= 80) reasons.push(`Built for ${answers.climate} climate zones`);
  if (priority >= 85) {
    const map = { efficiency: 'Top efficiency rating', quiet: 'Whisper-quiet operation', longevity: 'Excellent tank warranty', value: 'Strong rebate-eligible value' };
    reasons.push(map[answers.priority]);
  }
  if (recovery >= 85 && answers.fastRecovery === 'yes') reasons.push('Boost element for fast recovery');

  return { total, breakdown: { tank, climate, priority, recovery }, reasons };
};

// Rank selected products against wizard answers
export const rankProducts = (selectedProducts, answers) =>
  selectedProducts
    .map((p) => ({ product: p, ...scoreProduct(p, answers) }))
    .sort((a, b) => b.total - a.total);

// Helper: build a deep-link URL for the estimate page carrying selected models
export const buildEstimateUrl = (slugs) =>
  `/get-estimate?models=${encodeURIComponent(slugs.join(','))}`;

// Helper: resolve a list of slugs to product objects (filters invalid)
export const slugsToProducts = (slugs) =>
  slugs.map((s) => products[s]).filter(Boolean);
